import type { EventTicketSummary } from '@pulsify/shared/dto'
import { formatMoney } from '~/entities/ticket/lib/ticket-earnings'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Card } from '~/shared/ui/card'
import { countLabel, percentLabel } from '../lib/order-format'
import { fillRatio } from '../lib/ticket-figures'
import { ProgressBar } from './order-parts'

interface Figure {
  key: string
  label: string
  value: string
  suffix?: string
  ratio?: number | null
  accent?: boolean
  live?: boolean
  note?: string | null
}

function figuresOf(summary: EventTicketSummary, live: boolean, locale: string): Figure[] {
  const money = (cents: number) => formatMoney(cents, summary.currency, locale)
  const count = (value: number) => countLabel(value, locale)
  const checkedShare = fillRatio(summary.checkedIn, summary.issued)
  const soldLabel = summary.sellsPaid ? m.ticketPageSold() : m.ticketPageTaken()
  const ordersNote =
    summary.orderCount === 1
      ? m.ticketPageOrderCountOne()
      : m.ticketPageOrderCount({ count: count(summary.orderCount) })

  const sold: Figure = {
    key: 'sold',
    label: soldLabel,
    value: count(summary.issued),
    suffix:
      summary.capacity === null
        ? undefined
        : m.ticketPageOfTotal({ total: count(summary.capacity) }),
    ratio: fillRatio(summary.issued, summary.capacity),
    note:
      summary.sellsPaid && summary.freeIssued > 0
        ? m.ticketPageFreeShare({ count: count(summary.freeIssued) })
        : summary.capacity === null
          ? m.ticketPageNoLimit()
          : null,
  }
  const checkedIn: Figure = {
    key: 'checked-in',
    label: m.ticketPageCheckedIn(),
    value: count(summary.checkedIn),
    suffix: m.ticketPageOfTotal({ total: count(summary.issued) }),
    ratio: checkedShare ?? 0,
    accent: true,
    live,
    note: live
      ? m.ticketPageLiveNote()
      : checkedShare === null
        ? null
        : m.ticketPageCheckedInShare({ percent: percentLabel(checkedShare, locale) }),
  }
  if (!summary.sellsPaid) {
    return [
      sold,
      checkedIn,
      { key: 'orders', label: m.ticketPageOrders(), value: count(summary.orderCount) },
    ]
  }
  return [
    sold,
    checkedIn,
    { key: 'gross', label: m.earningsGross(), value: money(summary.grossCents), note: ordersNote },
    {
      key: 'net',
      label: m.earningsNet(),
      value: money(summary.netCents),
      note: summary.feeCents > 0 ? m.earningsAfterFee({ fee: money(summary.feeCents) }) : null,
    },
    {
      key: 'refunded',
      label: m.ticketsOrderRefunded(),
      value: money(summary.refundedCents),
      note:
        summary.refundedOrders === 0
          ? m.ticketPageRefundedNone()
          : summary.refundedOrders === 1
            ? m.ticketPageRefundedOrdersOne()
            : m.ticketPageRefundedOrders({ count: count(summary.refundedOrders) }),
    },
  ]
}

export function TicketFiguresCard({
  summary,
  live,
  locale,
}: {
  summary: EventTicketSummary
  live: boolean
  locale: string
}) {
  const figures = figuresOf(summary, live, locale)
  return (
    <Card className="overflow-hidden">
      <div
        className={cn(
          'grid grid-cols-2 gap-px bg-border',
          figures.length === 5 ? 'xl:grid-cols-5' : 'lg:grid-cols-3',
        )}
      >
        {figures.map((figure, index) => (
          <div
            key={figure.key}
            className={cn(
              'flex min-w-0 flex-col gap-2 bg-card p-4 sm:p-5',
              index === 0 &&
                (figures.length === 5 ? 'col-span-2 xl:col-span-1' : 'col-span-2 lg:col-span-1'),
            )}
          >
            <p className="flex items-center gap-1.5 truncate font-medium text-muted-foreground text-xs uppercase tracking-wider">
              {figure.label}
              {figure.live ? (
                <span className="inline-flex items-center gap-1 text-brand-subtle-foreground normal-case tracking-normal">
                  <span aria-hidden className="size-1.5 rounded-full bg-brand" />
                  {m.ticketPagePhaseLive()}
                </span>
              ) : null}
            </p>
            <p className="flex min-w-0 items-baseline gap-1.5">
              <span className="truncate font-semibold font-serif text-2xl text-foreground tabular-nums leading-none tracking-tight">
                {figure.value}
              </span>
              {figure.suffix ? (
                <span className="truncate text-muted-foreground text-sm tabular-nums">
                  {figure.suffix}
                </span>
              ) : null}
            </p>
            {figure.ratio === undefined || figure.ratio === null ? null : (
              <ProgressBar
                ratio={figure.ratio}
                label={`${figure.label} ${figure.value} ${figure.suffix ?? ''}`.trim()}
                accent={figure.accent}
              />
            )}
            {figure.note ? (
              <p className="text-muted-foreground text-xs leading-snug">{figure.note}</p>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  )
}
